import { DecimalPipe, AsyncPipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, OnDestroy, OnInit, inject } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { BehaviorSubject, Observable, Subject, distinctUntilChanged, map, takeUntil } from 'rxjs';
import { Blade } from 'src/app/modules/blades/models/blade-component';
import { BladeService } from 'src/app/modules/blades/services/blade.service';
import { VehicleStore } from 'src/app/modules/core/store';
import { WidgetsService } from '@components/widgets/widgets.service';
import { ColumnFactory } from 'src/app/modules/shared/helpers/column-factory';
import { CompactListColumn, CompactListItem, DeviationFilter, Vehicle, VehicleWarningConfig } from 'src/app/modules/shared/models';
import { WarningMessageType } from 'src/app/modules/shared/models/vehicle/vehicle-warning';
import { marker as _translate } from '@biesbjerg/ngx-translate-extract-marker';
import { WarningMessageHelper } from 'src/app/modules/shared/helpers/warning-message-helper';
import { FeedbackTextComponent } from '../../modules/shared/components/feedback-text/feedback-text.component';
import { CompactListComponent } from '../../modules/shared/components/compact-list/compact-list.component';
import { TabsComponent } from '../../modules/shared/components/tabs/tabs.component';
import { DeviationFilterComponent } from '../../modules/shared/components/deviation-filter/deviation-filter.component';
import { MatIconModule } from '@angular/material/icon';
import { WarningIconComponent } from '../../modules/shared/components/warning-icon/warning-icon.component';
import { BladeHeaderComponent } from '../../modules/blades/components/blade-header/blade-header.component';
import { LoggingHelperService } from '@modules/shared/helpers/logging-helper.service';


import * as _ from 'lodash';
import moment from 'moment';

@Component({
    selector: 'app-vehicle-warning',
    templateUrl: './vehicle-warning.component.html',
    styleUrls: ['./vehicle-warning.component.scss'],
    imports: [
        AsyncPipe,
        BladeHeaderComponent,
        WarningIconComponent,
        MatIconModule,
        DeviationFilterComponent,
        TabsComponent,
        CompactListComponent,
        FeedbackTextComponent,
        DecimalPipe,
        TranslateModule
    ],
    changeDetection: ChangeDetectionStrategy.Eager
})
@Blade({
    name: 'VehicleWarningComponent'
})
export class VehicleWarningComponent implements OnInit, OnDestroy {
    private readonly _route = inject(ActivatedRoute);
    private readonly _router = inject(Router);
    private readonly _bladeService = inject(BladeService);
    private readonly _vehicleStore = inject(VehicleStore);
    private readonly _warningMsgHelper = inject(WarningMessageHelper);
    private readonly _loggingHelperService = inject(LoggingHelperService);
    private readonly _widgetsService = inject(WidgetsService);
    
    bladeIndex: number;
    messageType: string;

    public title$: Observable<string|undefined>;
    public WarningTypeEnum = WarningMessageType;

    public warningType: number;
    public routeNumber: number;
    public garageCode: string;

    public componentState: WarningComponentState;
    public vehicleWarning$: Observable<Vehicle[]>;

    public deviationsFilter: DeviationFilter = new DeviationFilter();
    public filterExpanded = false;

    public columns: CompactListColumn[];
    public items: CompactListItem[];

    private _vehicles: Vehicle[];
    private _vehicleColumns: CompactListColumn[];
    private _warningColumns: CompactListColumn[];
    private _selectedVehicleInventoryNr: string;

    private readonly _title$: BehaviorSubject<string|undefined> = new BehaviorSubject(undefined);
    private readonly _ngUnsubscribe$ = new Subject<void>();
    private readonly _sourceComponentName = 'VehicleWarningComponent';
    private readonly _stateKey = 'warning-component-state';


    constructor() {
        this._route.params.pipe(
            takeUntil(this._ngUnsubscribe$)
        ).subscribe(params => {
            this.warningType = parseFloat(params['warningType']);

            this.messageType = this._getMessageType();
            this._title$.next(this._warningMsgHelper.getTitle(this.warningType));

            this._initColumnDefaults();
            this._updateItemsInList();
        });

        this._title$.next(this._warningMsgHelper.getTitle(this.warningType));
    }

    public get shouldShowDeviationFilter() {
        return this.componentState.selectedTab === 'warnings' && (
            (this.warningType === WarningMessageType.VehicleDelay) ||
            (this.warningType === WarningMessageType.TripNotStartedWarning) ||
            (this.warningType === WarningMessageType.SocWarning));
    }

    ngOnInit(): void {
        this._initColumnDefaults();
        this.deviationsFilter = new DeviationFilter();
        this.componentState = this._getComponentState();

        this._vehicleStore.getVehiclesState().pipe(
            map(vehicles =>
                _.chain(vehicles)
                    .filter(vehicle => this._vehicleHasCorrectWarning(vehicle))
                    .value()
            ),
            distinctUntilChanged(_.isEqual),
            takeUntil(this._ngUnsubscribe$)
        ).subscribe((vehiclesWithWarning) => {
            this._vehicles = vehiclesWithWarning;
            this._updateItemsInList();
        });

        // to set selection for vehicles
        this._bladeService.allOutletParams$.pipe(
            takeUntil(this._ngUnsubscribe$)
        ).subscribe(params => {
            const inventoryNumber = params['inventoryNumber'];
            this._selectedVehicleInventoryNr = inventoryNumber;
    
            this._resetSelection();
    
            if (inventoryNumber) {
                this._selectItem(inventoryNumber);
            }
        });

        this._loggingHelperService.logPageViewCount('VehicleWarning', this._router.url);
    }

    ngOnDestroy() {
        this._ngUnsubscribe$.next();
        this._ngUnsubscribe$.complete();
        this._resetVehicleSelection();
    }

    public async changeTab(selectedTab: string) {
        this.componentState.selectedTab = selectedTab;
        this._updateComponentState();
        this._updateItemsInList();
    }

    public vehicleItemSelected(item: CompactListItem) {
        const encodedItemId = encodeURIComponent(item.id);
        const indexOfItemId = this._router.routerState.snapshot.url.indexOf(`outlet-right0:vehicle/${encodedItemId}`);
        if (indexOfItemId === -1) {
            this._widgetsService.openVehicle(String(item.id));
        }
    }

    public vehicleColumnsUpdated(columns: CompactListColumn[]) {
        this._updateComponentState();
    }

    // Handle filter
    public onDeviationFilterChanged(deviationsFilter: DeviationFilter) {
        this.deviationsFilter = deviationsFilter;
        this._updateItemsInList();
        this._updateComponentState();
    }

    private _updateItemsInList() {
        if (this.componentState?.selectedTab) {
            this._setCompactList(this.componentState?.selectedTab);
        }
    }

    // Function for setting the correct items for the list depending on state
    private _setCompactList(selectedTab: string) {
        let newItems: CompactListItem[];
        
        if (selectedTab === 'warnings') {
            newItems = this._mapWarningItems(this._vehicles);
            this.columns = this._warningColumns;
        } else if (selectedTab === 'vehicles') {
            newItems = this._mapVehicleItems(this._vehicles);
            this.columns = this._vehicleColumns;
        }

        // filter items depending on filter?
        if (this.shouldShowDeviationFilter && newItems) {
            newItems = this._filterItemsArray(newItems);
        }

        // Defer assignment to prevent ExpressionChangedAfterItHasBeenCheckedError
        setTimeout(() => {
            this.items = newItems;
            
            // select item if selected exists
            if (this._selectedVehicleInventoryNr) {
                this._selectItem(this._selectedVehicleInventoryNr);
            }

            // Select all the vehicles from the filtered list in the map
            const inventoryNumbers = _.map(this.items, item => item.properties['inventoryNumber']);
            this._selectVehicles(inventoryNumbers);
        }, 0);
    }

    private _filterItemsArray(items: CompactListItem[]): CompactListItem[] {
        if (this.warningType === WarningMessageType.VehicleDelay) {
            return _.filter(items, (item) => !this.deviationsFilter.totalSeconds
                || item.object.metadata['riskOfDelayInSeconds'] >= this.deviationsFilter.totalSeconds);
        }
        if (this.warningType === WarningMessageType.TripNotStartedWarning || this.warningType === WarningMessageType.SocWarning) {
            return _.filter(items, (item) => !(new Date(item.object.measuredAt)).getTime()
                || new Date(item.object.measuredAt).getTime() <= Date.now() - (this.deviationsFilter.totalSeconds * 1000));
        }
        return items;
    }

    private _mapVehicleItems(vehicles: Vehicle[]) {
        return vehicles.map(vehicle => {
            const item = new CompactListItem();
            item.id = vehicle.inventoryNumber;
            item.selected = this._selectedVehicleInventoryNr === item.id;
            item.type = 'vehicle';
            item.properties['inventoryNumber'] = vehicle.inventoryNumber;
            item.properties['inventoryNumberWithoutPrefix'] = vehicle.inventoryNumberWithoutPrefix;
            item.properties['garageCode'] = vehicle.garageCode;
            item.properties['speed'] = vehicle.position?.speed;
            item.properties['bearing'] = vehicle.position?.bearing;
            item.properties['stop'] = vehicle.departureDeviation?.stopName;
            item.properties['tripNumber'] = vehicle.tripNumber;
            item.properties['blockNumber'] = vehicle.blockNumber;
            item.properties['warnings'] = vehicle.displayableWarnings?.length ?? 0;
            item.properties['deviation'] = vehicle.departureDeviation?.deviation.toShortString();
            item.properties['deviationseconds'] = vehicle.departureDeviation?.deviation.getTotalSeconds();
            item.properties['color'] = vehicle.departureDeviation?.color;
            item.properties['warningList'] = _.map(vehicle.warnings, w => w.messageType) || [];

            return item;
        });
    }

    private _mapWarningItems(vehicles: Vehicle[]) {
        return _.chain(vehicles)
            .flatMap(v => _.map(v.displayableWarnings, w => (
                {
                    inventoryNumber: v.inventoryNumber,
                    warning: w
                })))
            .filter(x => this._validWarningType(x.warning.messageType))
            .sortBy(x => 1 - x.warning.severityRatio)
            .map(x => {
                const item = new CompactListItem();
                item.id = x.inventoryNumber;
                item.selected = this._selectedVehicleInventoryNr === item.id;
                item.type = 'vehicle';
                item.object = x.warning;
                item.properties['inventoryNumber'] = this._removeNonNumericCharacters(x.inventoryNumber);
                item.properties['warningIcon'] = x.warning.messageType;
                item.properties['warningText'] = this._warningMsgHelper.getShortDescription(x.warning);

                const measuredAt = moment(x.warning.measuredAt).format('HH:mm');
                item.properties['measuredAt'] = measuredAt;
                item.properties['measuredAtDate'] = x.warning.measuredAt;

                if (this.warningType === WarningMessageType.VehicleDelay) {
                    const delay = x.warning.metadata['riskOfDelayInSeconds'] ?? 0;
                    item.properties['time'] = this._secondsToHHMM(delay);
                    item.properties['timeSeconds'] = delay;
                } 
                if ([
                    WarningMessageType.TripNotStartedWarning,
                    WarningMessageType.OffRouteWarning,
                    WarningMessageType.SignOnMissingWarning
                ].includes(this.warningType)) {
                    item.properties['routeNumber'] = x.warning.metadata['routeName'];
                    item.properties['tripNumber'] = x.warning.metadata['tripNumber'];
                }

                return item;
            })
            .value();
    }

    private _selectItem(id: string) {
        if (!this.items) {
            return;
        }

        const found = _.filter(this.items, item => item.id === id);
        if (found) {
            found.forEach(item => item.selected = true);
        }
    }

    private _removeNonNumericCharacters(str: string): string {
        if (!str) return '';
        
        // remove prefix for norwegan and finnish vehicles
        const charsToRemove = /\D/g;
        
        if (charsToRemove.test(str)) {
            return str.replace(charsToRemove, '');
        }

        return str;
    }

    private _resetSelection() {
        // reset selection
        const selectedItems = _.chain(this.items)
            .filter(item => item.selected)
            .value();
        selectedItems.forEach(item => item.selected = false);
    }

    private _selectVehicles(inventoryNumbers: string[]) {
        this._vehicleStore.selectVehicles(inventoryNumbers, this._sourceComponentName);
    }

    private _resetVehicleSelection() {
        this._vehicleStore.resetVehiclesSelection(this._sourceComponentName);
    }

    private _initColumnDefaults() {
        this._vehicleColumns = [
            ColumnFactory.getInventoryColumn({ sort: 'asc' }),
            ColumnFactory.getTripNumberColumn(),
            ColumnFactory.getBlockNumberColumn(),
            ColumnFactory.getDeviationColumn(),
            ColumnFactory.getNextStopColumn({ width: 100 }),
            ColumnFactory.getBearingColumn(),
            ColumnFactory.getWarningColumn()
        ];

        this._warningColumns = [
            ColumnFactory.getWarningIconColumn(),
            ColumnFactory.getInventoryWithPrefixColumn(),
            ColumnFactory.getWarningTextColumn(),
        ];

        if (this.warningType === WarningMessageType.VehicleDelay) {
            this._warningColumns.push(ColumnFactory.getTimeColumn({
                sort: 'desc',
                name: _translate('Delay')
            }));
        } else if (this.warningType === WarningMessageType.TripNotStartedWarning) {
            this._warningColumns.push(ColumnFactory.getRouteNumberColumn());
            this._warningColumns.push(ColumnFactory.getTripNumberColumn());
        } else if ([
            WarningMessageType.OffRouteWarning, 
            WarningMessageType.SignOnMissingWarning
        ].includes(this.warningType)) {
            this._warningColumns.push(ColumnFactory.getRouteNumberColumn());
            this._warningColumns.push(ColumnFactory.getTripNumberColumn());
            this._warningColumns.push(ColumnFactory.getMeasuredAtColumn());
        }
    }

    private _getComponentState(): WarningComponentState {
        const storedState = localStorage.getItem(this._stateKey);
        const state = new WarningComponentState();

        state.selectedTab = 'warnings';
        state.tabs = ['warnings', 'vehicles'];

        // Get the selected tab from the localstorage and set as active if the tab exists in the tabs-list
        if (storedState) {
            const storedVehicleComponentState: WarningComponentState = JSON.parse(storedState);
            const storedSelectedTab = storedVehicleComponentState.selectedTab;
            if (_.includes(state.tabs, storedSelectedTab)) {
                state.selectedTab = storedSelectedTab;
            }

            this._vehicleColumns = _.map(this._vehicleColumns, (column) => {
                const storedColumn = _.find(storedVehicleComponentState.vehicleColumns, (c) => c.key === column.key);
                if (storedColumn) {
                    delete column.sort;
                    delete storedColumn.width;
                    delete storedColumn.icon;
                }
                return { ...column, ...storedColumn };
            });

            this._warningColumns = _.map(this._warningColumns, (column) => {
                const storedColumn = _.find(storedVehicleComponentState.warningColumns, (c) => c.key === column.key);
                if (storedColumn) {
                    delete column.sort;
                    delete storedColumn.width;
                    delete storedColumn.icon;
                }
                return { ...column, ...storedColumn };
            });

            if (storedVehicleComponentState.deviationFilter) {
                this.deviationsFilter = new DeviationFilter();
                this.deviationsFilter.seconds = storedVehicleComponentState.deviationFilter.seconds ?? 0;
                this.deviationsFilter.minutes = storedVehicleComponentState.deviationFilter.minutes ?? 5;
                this.deviationsFilter.hours = storedVehicleComponentState.deviationFilter.hours ?? 0;
            }

        }
        return state;
    }

    private _updateComponentState() {
        this.componentState.deviationFilter = this.deviationsFilter;
        localStorage.setItem(this._stateKey, JSON.stringify(this.componentState));
    }

    private _vehicleHasCorrectWarning(vehicle: Vehicle) {
        return _.filter(
            vehicle.warnings,
            warning => this._validWarningType(warning.messageType)).length > 0;
    }

    private _validWarningType(warningType: WarningMessageType) {
        if (this.warningType === WarningMessageType.Other) {
            return !_.includes(VehicleWarningConfig.warningsWithOwnCategories, warningType);
        } else {
            return warningType === this.warningType;
        }
    }

    private _getMessageType() {
        return this.warningType.toString();
    }

    private _secondsToHHMM(totalSeconds: number): string {
        const hours: number = Math.floor(totalSeconds / 3600);
        const minutes: number = Math.floor((totalSeconds % 3600) / 60);
        const seconds: number = totalSeconds % 60;

        const formattedHours: string = hours < 10 ? `0${hours}` : `${hours}`;
        const formattedMinutes: string = minutes < 10 ? `0${minutes}` : `${minutes}`;
        const formattedSeconds: string = seconds < 10 ? `0${seconds}` : `${seconds}`;

        return `${formattedHours}:${formattedMinutes}:${formattedSeconds}`;
    }
}

export class WarningComponentState {
    tabs: string[];
    selectedTab: string;
    vehicleColumns: CompactListColumn[];
    warningColumns: CompactListColumn[];
    deviationFilter: DeviationFilter;
}
